import {Component, effect, EffectRef, Signal, signal, TemplateRef, ViewChild, AfterViewInit, OnDestroy} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ActivatedRoute, Router, RouterModule} from '@angular/router';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {faEdit, faLink, faList, faRobot, faStar, faTableCells, faUser, faUsers, faUtensils} from '@fortawesome/free-solid-svg-icons';
import {RecipeListFilters, RecipeListItem, RecipeService, RecipeSourceDto} from '../../services/recipe/recipe.service';
import {RecipeFacetsService} from '../../services/recipe/recipe-facets.service';
import {RecipeFacetsCatalogue} from '../../services/recipe/recipe-facets.models';
import {RecipeFacetBadgesComponent} from '../../components/recipe-facets/recipe-facet-badges.component';
import {RecipeFacetFilterBarComponent} from '../../components/recipe-facets/recipe-facet-filter-bar.component';
import {AppEventsService} from '../../services/app-events/app-events.service';
import {ContextService} from '../../services/context/context-signal.service';
import {Context} from '../../models/context';
import {PageHeaderService} from '../../services/page-header/page-header.service';
import {PageAction} from '../../components/app-shell/page-action.model';
import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {
  UiButtonComponent,
  UiButtonGroupComponent,
  UiInputComponent,
  UiListComponent,
  UiListItemComponent,
  UiModalComponent,
  UiRecipePreviewComponent,
  UiStarBarComponent
} from '../../shared/ui';
import { AppLogger, AppLoggerService } from '../../core/logging';
import { ImportImageSelectionDto, RecipeDraftDto, RecipeImportJobDto, RecipeImportService } from '../../services/recipe/recipe-import.service';

/** A proposed import image plus the user's review state and its preview object
 *  URL (fetched as a blob because the endpoint is auth-protected). */
interface ProposedImageView {
  name: string;
  role: string;
  defaultTitle: boolean;
  score?: number;
  keep: boolean;
  isTitle: boolean;
  objectUrl: string | null;
}

@Component({
  selector: 'app-recipes',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FontAwesomeModule,
    UiInputComponent,
    UiButtonComponent,
    UiButtonGroupComponent,
    UiListComponent,
    UiListItemComponent,
    UiModalComponent,
    UiStarBarComponent,
    UiRecipePreviewComponent,
    RecipeFacetBadgesComponent,
    RecipeFacetFilterBarComponent
  ],
  templateUrl: './recipes.component.html',
  styleUrl: './recipes.component.scss'
})
export class RecipesComponent implements AfterViewInit, OnDestroy {
  faUser = faUser;
  faUsers = faUsers;
  readonly faEdit = faEdit;
  readonly faLink = faLink;
  readonly faRobot = faRobot;
  readonly faStar = faStar;
  readonly faUtensils = faUtensils;
  readonly faList = faList;
  readonly faTableCells = faTableCells;

  recipes = signal<RecipeListItem[]>([]);
  // List (default) vs grid layout. Persisted per browser so the choice sticks.
  viewMode = signal<'list' | 'grid'>(this.readViewMode());
  isLoading = signal<boolean>(false);
  // Deferred mirror of `isLoading`: only true once loading has lasted past a
  // short grace period, so a fast reload (e.g. the list re-fetching behind the
  // import dialog) doesn't flash a spinner. Drives what the template renders.
  loadingVisible = signal<boolean>(false);
  private loadingDelayTimer?: ReturnType<typeof setTimeout>;
  errorMessage = signal<string | null>(null);

  view = signal<'all' | 'mine'>('all');

  // Facet catalogue + filters. Filter by KEY, render label.
  facetCatalogue = signal<RecipeFacetsCatalogue | null>(null);
  dishTypeLabels = signal<Map<string, string>>(new Map());
  courseFilter = signal<string>('');
  seasonFilter = signal<string>('');
  dishTypeFilter = signal<string>('');

  context: Signal<Context> = this.contextService.context;
  private householdId = signal<number | null>(null);
  private readonly loadEffect: EffectRef;

  searchTerm = '';
  private readonly searchInput$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  // Import modal
  @ViewChild('importUiModal', {static: false}) importUiModal?: UiModalComponent;
  importUrl = signal<string>('');
  importError = signal<string | null>(null);
  importIsStarting = signal<boolean>(false);
  importJob = signal<RecipeImportJobDto | null>(null);
  importIsPolling = signal<boolean>(false);
  importIsSaving = signal<boolean>(false);
  // Object URL of the crawler-captured page thumbnail, shown in the "fake
  // browser" loading view while the AI scans the page. Null until captured.
  importThumbnailUrl = signal<string | null>(null);
  private importPollTimer?: ReturnType<typeof setInterval>;
  private importThumbnailPollTimer?: ReturnType<typeof setInterval>;
  private importThumbnailObjectUrl: string | null = null;
  // Proposed images for the current draft (title + others), with review state.
  proposedImages = signal<ProposedImageView[]>([]);

  @ViewChild('toolbarSearch') toolbarSearchTemplate!: TemplateRef<any>;

  private readonly log: AppLogger;

  constructor(
    private recipeService: RecipeService,
    private recipeFacetsService: RecipeFacetsService,
    private recipeImportService: RecipeImportService,
    private contextService: ContextService,
    private pageHeaderService: PageHeaderService,
    private appEvents: AppEventsService,
    private appLogger: AppLoggerService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.log = this.appLogger.createForClass(RecipesComponent);

    this.loadEffect = effect(() => {
      const c = this.context();
      if (!c?.isSet?.()) return;
      this.householdId.set(c.household.id);
      this.loadRecipes();
    });

    this.searchInput$
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(term => {
        this.searchTerm = term?.trim() ?? '';
        // Server-side search: re-query with the `q` param rather than filtering
        // the already-loaded page client-side.
        this.loadRecipes();
      });

    // A recipe created elsewhere (e.g. the household chat assistant's
    // propose_recipe -> confirm flow) reports back via AffectedResource
    // "recipe"; refresh the list so it shows up without a manual reload.
    this.appEvents.recipeChanged$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.loadRecipes());

    // The list/grid layout lives in the `?layout=` query param so it's
    // bookmarkable/shareable and survives refresh. Absent param falls back to
    // the per-browser localStorage preference. Reacting to the param (rather
    // than reading it once) also keeps browser back/forward in sync.
    this.route.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const layout = params.get('layout');
        const mode: 'list' | 'grid' =
          layout === 'grid' ? 'grid' :
          layout === 'list' ? 'list' :
          this.readViewMode();
        this.viewMode.set(mode);
      });

    // Facet catalogue drives the filter dropdowns and resolves dish-type keys
    // (list items carry dish types as bare keys) to their labels for the badges.
    this.recipeFacetsService.getCatalogue()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (cat) => {
          this.facetCatalogue.set(cat);
          const map = new Map<string, string>();
          for (const c of cat.dish_types ?? []) {
            map.set(c.key, c.name_de || c.name_en || c.key);
          }
          this.dishTypeLabels.set(map);
        },
        error: (err) => this.log.error('Error loading recipe facet catalogue', err),
      });
  }

  onCourseFilterChange(key: string): void {
    this.courseFilter.set(key ?? '');
    this.loadRecipes();
  }

  onSeasonFilterChange(key: string): void {
    this.seasonFilter.set(key ?? '');
    this.loadRecipes();
  }

  onDishTypeFilterChange(key: string): void {
    this.dishTypeFilter.set(key ?? '');
    this.loadRecipes();
  }

  hasActiveFacetFilters(): boolean {
    return !!(this.courseFilter() || this.seasonFilter() || this.dishTypeFilter());
  }

  clearFacetFilters(): void {
    if (!this.hasActiveFacetFilters()) {
      return;
    }
    this.courseFilter.set('');
    this.seasonFilter.set('');
    this.dishTypeFilter.set('');
    this.loadRecipes();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.pageHeaderService.setPageTitle('Recipes');

      if (this.toolbarSearchTemplate) {
        this.pageHeaderService.setSearch(this.toolbarSearchTemplate);
      }

      this.refreshPageActions();
      this.maybeOpenImportFromRoute();
    }, 0);
  }

  /**
   * Auto-open the import dialog when the component is entered via the
   * `/recipes/import` deep link (fresh load, refresh, or the Import button's
   * navigation). An optional `?url=` query param pre-fills the URL box.
   */
  private maybeOpenImportFromRoute(): void {
    if (!this.isOnImportRoute()) return;

    const url = this.route.snapshot.queryParamMap.get('url');
    if (url) {
      this.importUrl.set(url);
    }
    this.openImportFromUrlModal();
  }

  /** True when the current URL path is the import deep link. */
  private isOnImportRoute(): boolean {
    return this.router.url.split('?')[0] === '/recipes/import';
  }

  /**
   * Declarative toolbar actions:
   *  - Import: secondary, pinned (always visible).
   *  - New: primary, pinned (always visible). Navigates to /recipe/new.
   */
  private refreshPageActions(): void {
    const actions: PageAction[] = [
      {
        type: 'button',
        id: 'import',
        label: 'Import',
        icon: this.faLink,
        variant: 'secondary',
        pinned: true,
        // Navigate to a dedicated route so the import dialog is deep-linkable:
        // a refresh reopens it, and an optional ?url= pre-fills the box.
        onClick: () => this.router.navigate(['/recipes/import']),
      },
      {
        type: 'button',
        id: 'new',
        label: 'New',
        icon: this.faEdit,
        variant: 'primary',
        pinned: true,
        onClick: () => this.router.navigate(['/recipe/new']),
      },
    ];
    this.pageHeaderService.setPageActions(actions);
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearAll();
    this.destroy$.next();
    this.destroy$.complete();
    this.stopPollingImportJob();
    this.clearImportThumbnail();
    this.clearProposedImages();
    this.clearLoadingSpinnerDelay();
  }

  // --- Import from URL ---

  openImportFromUrlModal(): void {
    this.importError.set(null);
    this.importIsStarting.set(false);
    this.importJob.set(null);
    this.importIsPolling.set(false);
    this.importIsSaving.set(false);
    this.clearImportThumbnail();
    this.clearProposedImages();

    // keep last URL if any, but trim whitespace
    this.importUrl.set((this.importUrl() ?? '').trim());

    // ensure modal is ready
    setTimeout(() => this.importUiModal?.open(), 0);
  }

  // Set right before we intentionally close the dialog to navigate elsewhere
  // (e.g. opening the imported recipe), so the dismiss handler doesn't also
  // bounce back to /recipes.
  private navigatingAfterImport = false;

  closeImportFromUrlModal(): void {
    // Closing emits (modalClosed) → onImportDialogDismissed(), which stops
    // polling and syncs the URL back to /recipes.
    this.importUiModal?.close();
  }

  /**
   * Fired whenever the import dialog closes (Cancel, backdrop, or ✕). Stops
   * polling and — when we arrived via the /recipes/import deep link — returns
   * the URL to /recipes so a refresh won't reopen the dialog.
   */
  onImportDialogDismissed(): void {
    this.stopPollingImportJob();

    if (this.navigatingAfterImport) {
      this.navigatingAfterImport = false;
      return;
    }

    if (this.isOnImportRoute()) {
      this.router.navigate(['/recipes']);
    }
  }

  onImportUrlChange(url: string): void {
    this.importUrl.set(url ?? '');
  }

  startImportFromUrl(): void {
    const url = (this.importUrl() ?? '').trim();
    if (!url) {
      this.importError.set('Please enter a URL.');
      return;
    }

    const householdId = this.householdId();
    if (!householdId) {
      this.importError.set('No household selected.');
      return;
    }

    this.importError.set(null);
    this.importIsStarting.set(true);

    this.recipeImportService.startImport(url, householdId).subscribe({
      next: (res) => {
        const job = res?.job;
        if (!job?.id) {
          this.importIsStarting.set(false);
          this.importError.set('Import started, but no job id was returned.');
          return;
        }

        this.importJob.set(job);
        this.importIsStarting.set(false);

        this.startPollingImportJob(job.id);
      },
      error: (err) => {
        this.importIsStarting.set(false);
        this.importError.set('Could not start import.');
        this.log.error('Error starting recipe import', err, { url, householdId });
      }
    });
  }

  private startPollingImportJob(jobId: string): void {
    this.stopPollingImportJob();

    this.importIsPolling.set(true);

    // Fetch the page capture in parallel so the loading view can switch from
    // "opening page…" to the scanning animation as soon as it's available.
    this.startPollingImportThumbnail(jobId);

    const pollOnce = () => {
      this.recipeImportService.getImportJob(jobId).subscribe({
        next: (res) => {
          const job = res?.job ?? null;
          this.importJob.set(job);

          const status = job?.status;
          if (status === 'draft_ready' || status === 'succeeded' || status === 'failed') {
            this.stopPollingImportJob();
            // refresh list after successful import (new recipe likely exists)
            if (status === 'succeeded') {
              this.loadRecipes();
            }
            // The pipeline can finish "successfully" with no usable draft (e.g.
            // the AI couldn't find a recipe on the page) - without this, the UI
            // just shows a dead-end "draft_ready" status with nothing to review.
            if (status === 'draft_ready' && !job?.draft) {
              this.importError.set(
                'Import finished, but no recipe could be extracted from that page. Try a different URL.'
              );
            }
            if (status === 'draft_ready' && job?.draft) {
              this.loadProposedImages(job);
            }
          }
        },
        error: (err) => {
          // Keep polling, but surface error.
          this.importError.set('Could not fetch import status. Retrying...');
          this.log.error('Error polling recipe import job', err, { jobId });
        }
      });
    };

    pollOnce();
    // Poll every 1s; easy on backend while staying responsive.
    this.importPollTimer = setInterval(pollOnce, 1000);
  }

  private stopPollingImportJob(): void {
    if (this.importPollTimer) {
      clearInterval(this.importPollTimer);
      this.importPollTimer = undefined;
    }
    this.stopPollingImportThumbnail();
    this.importIsPolling.set(false);
  }

  /**
   * Poll the thumbnail endpoint until the crawler has captured the page. The
   * backend returns 425 (surfaced as an HTTP error here) until the artifact
   * exists, so we simply retry until we get an image, then stop.
   */
  private startPollingImportThumbnail(jobId: string): void {
    this.stopPollingImportThumbnail();

    const tryFetch = () => {
      this.recipeImportService.getImportJobThumbnail(jobId).subscribe({
        next: (blob) => {
          if (!blob || blob.size === 0) {
            return;
          }
          this.setImportThumbnail(blob);
          this.stopPollingImportThumbnail();
        },
        error: () => {
          // 425 Too Early (page not captured yet) or transient — keep polling.
        },
      });
    };

    tryFetch();
    this.importThumbnailPollTimer = setInterval(tryFetch, 800);
  }

  private stopPollingImportThumbnail(): void {
    if (this.importThumbnailPollTimer) {
      clearInterval(this.importThumbnailPollTimer);
      this.importThumbnailPollTimer = undefined;
    }
  }

  private setImportThumbnail(blob: Blob): void {
    this.revokeImportThumbnailObjectUrl();
    const url = URL.createObjectURL(blob);
    this.importThumbnailObjectUrl = url;
    this.importThumbnailUrl.set(url);
  }

  private clearImportThumbnail(): void {
    this.stopPollingImportThumbnail();
    this.revokeImportThumbnailObjectUrl();
    this.importThumbnailUrl.set(null);
  }

  private revokeImportThumbnailObjectUrl(): void {
    if (this.importThumbnailObjectUrl) {
      URL.revokeObjectURL(this.importThumbnailObjectUrl);
      this.importThumbnailObjectUrl = null;
    }
  }

  /**
   * Build the review state from a draft-ready job's proposed images and fetch
   * each preview as a blob (the endpoint is auth-protected). Default selection
   * mirrors the backend default: the title image is kept and marked title;
   * others start unchecked, ready for the user to add.
   */
  private loadProposedImages(job: RecipeImportJobDto): void {
    this.clearProposedImages();

    const proposed = job.proposed_images ?? [];
    if (proposed.length === 0) {
      return;
    }

    const views: ProposedImageView[] = proposed.map(p => ({
      name: p.name,
      role: p.role,
      defaultTitle: !!p.default_title,
      score: p.score,
      keep: !!p.default_title,
      isTitle: !!p.default_title,
      objectUrl: null,
    }));
    // Guarantee exactly one title when the crawler didn't flag one.
    if (views.length > 0 && !views.some(v => v.isTitle)) {
      views[0].keep = true;
      views[0].isTitle = true;
    }
    this.proposedImages.set(views);

    for (const view of views) {
      this.recipeImportService.getImportJobImage(job.id, view.name).subscribe({
        next: (blob) => {
          if (!blob || blob.size === 0) {
            return;
          }
          const url = URL.createObjectURL(blob);
          this.proposedImages.update(list =>
            list.map(v => (v.name === view.name ? { ...v, objectUrl: url } : v))
          );
        },
        error: (err) => this.log.error('Error loading proposed image', err, { jobId: job.id, name: view.name }),
      });
    }
  }

  /** Toggle whether a proposed image is kept. Unkeeping the title moves the
   *  title to the first remaining kept image. */
  toggleKeepProposedImage(name: string): void {
    this.proposedImages.update(list => {
      let next = list.map(v => (v.name === name ? { ...v, keep: !v.keep, isTitle: !v.keep ? v.isTitle : false } : v));
      if (!next.some(v => v.keep && v.isTitle)) {
        const firstKept = next.find(v => v.keep);
        if (firstKept) {
          next = next.map(v => ({ ...v, isTitle: v.name === firstKept.name }));
        }
      }
      return next;
    });
  }

  /** Mark a proposed image as the title (which also keeps it). */
  setProposedTitle(name: string): void {
    this.proposedImages.update(list =>
      list.map(v => ({
        ...v,
        isTitle: v.name === name,
        keep: v.name === name ? true : v.keep,
      }))
    );
  }

  private clearProposedImages(): void {
    for (const v of this.proposedImages()) {
      if (v.objectUrl) {
        URL.revokeObjectURL(v.objectUrl);
      }
    }
    this.proposedImages.set([]);
  }

  private buildImageSelection(): ImportImageSelectionDto[] {
    return this.proposedImages().map(v => ({
      name: v.name,
      keep: v.keep,
      is_title: v.keep && v.isTitle,
    }));
  }

  /** Loading phase 1: the job is running but the page hasn't been captured yet. */
  isOpeningPage(): boolean {
    return this.isJobRunning(this.importJob()) && !this.importThumbnailUrl();
  }

  /** Loading phase 2: page captured — show the thumbnail with the scanning
   *  animation while the AI extracts the recipe. */
  isScanningPage(): boolean {
    return this.isJobRunning(this.importJob()) && !!this.importThumbnailUrl();
  }

  isJobRunning(job: RecipeImportJobDto | null): boolean {
    const st = (job?.status ?? '').toLowerCase();
    return st === 'running' || st === 'pending';
  }

  /** `draft_ready` is a distinct state (pipeline finished, nothing persisted
   *  yet, awaiting review) — not "running" and not "succeeded". */
  isDraftReady(job: RecipeImportJobDto | null): boolean {
    return (job?.status ?? '').toLowerCase() === 'draft_ready' && !!job?.draft;
  }

  /** The current job's draft, ready to hand to <ui-recipe-preview>, or
   *  null when there isn't one to review yet. Kept as a plain getter (rather
   *  than inlining `importJob()?.draft` with a type-guard in the template)
   *  so the `@if (...; as draft)` binding narrows to a non-null type. */
  importJobDraft(): RecipeDraftDto | null {
    const job = this.importJob();
    return this.isDraftReady(job) ? (job!.draft as RecipeDraftDto) : null;
  }

  saveImportedDraft(): void {
    const job = this.importJob();
    if (!job?.id || !this.isDraftReady(job)) {
      return;
    }

    this.importError.set(null);
    this.importIsSaving.set(true);

    this.recipeImportService.saveImportJobDraft(job.id, this.buildImageSelection()).subscribe({
      next: (res) => {
        this.importIsSaving.set(false);
        const savedJob = res?.job ?? null;
        this.importJob.set(savedJob);

        if (savedJob?.status === 'succeeded') {
          this.loadRecipes();
        }
      },
      error: (err) => {
        this.importIsSaving.set(false);
        this.importError.set('Could not save recipe.');
        this.log.error('Error saving recipe import draft', err, { jobId: job.id });
      }
    });
  }

  canViewImportedRecipe(job: RecipeImportJobDto | null): boolean {
    return (job?.status ?? '').toLowerCase() === 'succeeded' && !!job?.created_recipe_id;
  }

  viewImportedRecipe(job: RecipeImportJobDto | null): void {
    const id = job?.created_recipe_id;
    if (!id) return;

    // We're navigating straight to the recipe, so the dismiss handler must not
    // also redirect to /recipes.
    this.navigatingAfterImport = true;
    this.closeImportFromUrlModal();
    // imported recipe should land on edit
    this.router.navigate(['/recipe', id, 'edit']);
  }

  setView(view: 'all' | 'mine'): void {
    if (this.view() === view) {
      return;
    }
    this.view.set(view);
    this.loadRecipes();
  }

  /**
   * Switch between the list and grid layouts. The choice is written to the
   * `?layout=` query param (bookmarkable/shareable) and mirrored into
   * localStorage as the per-browser fallback for when the param is absent.
   * `replaceUrl` keeps toggling out of the browser history stack.
   */
  setViewMode(mode: 'list' | 'grid'): void {
    this.viewMode.set(mode);
    try {
      localStorage.setItem('recipes.viewMode', mode);
    } catch {
      // ignore storage failures (private mode etc.)
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { layout: mode },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private readViewMode(): 'list' | 'grid' {
    try {
      return localStorage.getItem('recipes.viewMode') === 'grid' ? 'grid' : 'list';
    } catch {
      return 'list';
    }
  }

  onSearch(term: string): void {
    this.searchInput$.next(term ?? '');
  }

  clearSearch(): void {
    if (!this.searchTerm) {
      return;
    }
    this.searchTerm = '';
    this.loadRecipes();
  }

  loadRecipes(): void {
    const householdId = this.householdId();
    if (!householdId) {
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);
    this.startLoadingSpinnerDelay();

    const viewParam = this.view() === 'mine' ? 'mine' : undefined;
    const q = this.searchTerm?.trim() || undefined;
    const filters: RecipeListFilters = {
      course: this.courseFilter() || undefined,
      season: this.seasonFilter() || undefined,
      dishType: this.dishTypeFilter() || undefined,
    };

    this.recipeService.getHouseholdRecipes(householdId, viewParam, q, filters).subscribe({
      next: (recipes) => {
        this.recipes.set(recipes ?? []);
        this.finishLoading();
      },
      error: (err) => {
        this.finishLoading();
        this.errorMessage.set('Could not load recipes.');
        this.log.error('Error loading recipes', err, { householdId, q });
      }
    });
  }

  /** Reveal the loading spinner only if loading outlasts the grace period. */
  private startLoadingSpinnerDelay(): void {
    this.clearLoadingSpinnerDelay();
    this.loadingDelayTimer = setTimeout(() => {
      if (this.isLoading()) {
        this.loadingVisible.set(true);
      }
    }, 200);
  }

  /** Settle the loading state and drop any pending spinner-reveal timer. */
  private finishLoading(): void {
    this.isLoading.set(false);
    this.loadingVisible.set(false);
    this.clearLoadingSpinnerDelay();
  }

  private clearLoadingSpinnerDelay(): void {
    if (this.loadingDelayTimer) {
      clearTimeout(this.loadingDelayTimer);
      this.loadingDelayTimer = undefined;
    }
  }

  sourceLabel(source?: RecipeSourceDto): string {
    if (!source) return 'Created';
    if (source.type === 'import') return 'Imported';
    if (source.type === 'assistant') return 'By Assistant';
    return 'Created';
  }

  sourceIcon(source?: RecipeSourceDto) {
    if (source?.type === 'import') return this.faLink;
    if (source?.type === 'assistant') return this.faRobot;
    return this.faUser;
  }
}
